import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSActionMenuDropdown,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconColor,
  MDSIconKind,
  MDSLink,
  MDSMenuContentItemKind,
  MDSMenuDropdownSlotName,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTooltipTrigger,
} from "@/mds";
import { api } from "@/modules/api";
import type { ApiSchema } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { formatCalendarSyncTime } from "@/components/calendar/calendar-connection-configuration-list/calendar-connection-configuration-list-item/calendar-connection-configuration-list-item.utils";
import {
  SettingsIntegrationHeader,
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import { StyledSubExplanation } from "@/pages/integrations/shared/integrations.styles";
import {
  trackMcpConnectionSetupAttempt,
  trackMcpConnectionSetupNetworkFailure,
} from "@/pages/settings/mcp-connection-observability";
import {
  MCP_CONNECTION_OAUTH_INCOMPATIBLE_MESSAGE,
  MCP_CONNECTION_PROACTIVE_REQUIRED_MESSAGE,
  MCP_CONNECTION_SYNC_FAILED_MESSAGE,
} from "@/pages/settings/mcp-connection-copy";
import { useAppStore } from "@/store";
import type { SpaceAccountConnectionModelData } from "@/store/connections/types";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import {
  CommercialMeterKey,
  CommercialMigrationState,
  getCompatibleCommercialCatalog,
  parseCommercialUsageLimitRejection,
} from "@/modules/commercial-plans";
import {
  FEATURED_COMPOSIO_CONNECTIONS,
  type FeaturedComposioConnection,
} from "./composio-connection-apps";

type McpConnectionResponse = ApiSchema["CoreApi__McpConnectionResponseSchema"];
type DisplayConnection = SpaceAccountConnectionModelData & {
  id: string;
  server_url: string;
  status: NonNullable<SpaceAccountConnectionModelData["status"]>;
};

function isDisplayConnection(
  connection: SpaceAccountConnectionModelData & { id: string }
): connection is DisplayConnection {
  return connection.server_url !== null && connection.status !== null;
}

const LEGACY_MAX_CONNECTIONS = 5;
const REFRESH_TIMEOUT_MS = 30_000;
const MCP_CONNECTION_NETWORK_ERROR_MESSAGE =
  "We couldn’t start the connection. Check your internet connection and try again.";
const PROACTIVE_REQUIRED_TOOLTIP = {
  label: MCP_CONNECTION_PROACTIVE_REQUIRED_MESSAGE,
  placement: MDSTooltipPlacement.Top,
};
const statusLabels: Record<DisplayConnection["status"], string> = {
  authorizing: "Waiting for authorization",
  connected: "Connected",
  needs_attention: "Needs attention",
};

function getConnectionDisplayName(connection: DisplayConnection): string {
  return connection.server_name || new URL(connection.server_url).host;
}

function getConnectionStatusDescription(connection: DisplayConnection): string | null {
  if (connection.error_kind === "oauth_incompatible") {
    return MCP_CONNECTION_OAUTH_INCOMPATIBLE_MESSAGE;
  }
  if (connection.provider === "composio" && connection.authorization_health === "expired") {
    return "Connection expired—reconnect to continue";
  }
  if (connection.status !== "connected") {
    return connection.error_message || statusLabels[connection.status];
  }
  if (connection.presentation_health === "sync_failed") {
    return MCP_CONNECTION_SYNC_FAILED_MESSAGE;
  }
  return null;
}

function getAuthorizationActionLabel(connection: DisplayConnection): "Reconnect" | "Retry" | null {
  if (connection.error_kind === "oauth_incompatible") return null;
  if (connection.provider === "composio" && connection.authorization_health === "expired") {
    return "Reconnect";
  }
  if (
    connection.provider === null &&
    connection.last_refreshed_at === null &&
    ((connection.status === "needs_attention" &&
      (connection.auth_method === "none" || connection.auth_method === "oauth")) ||
      (connection.status === "authorizing" && connection.auth_method === "oauth"))
  ) {
    return "Retry";
  }
  return null;
}

function getFeaturedComposioConnection(
  connection: DisplayConnection
): FeaturedComposioConnection | null {
  if (connection.provider !== "composio") return null;

  const serverName = connection.server_name?.trim().toLowerCase();
  const providerAppId = connection.provider_app_id?.trim().toLowerCase();

  return (
    FEATURED_COMPOSIO_CONNECTIONS.find(app => {
      return (
        serverName === app.title.toLowerCase() ||
        providerAppId?.startsWith(app.providerAppIdPrefix) === true
      );
    }) ?? null
  );
}

function getMcpConnectErrorMessage(error: unknown): string {
  if (error instanceof TypeError) {
    return MCP_CONNECTION_NETWORK_ERROR_MESSAGE;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Could not connect to that server.";
}

interface ProactiveConnectButtonProps {
  hasProactiveAccess: boolean;
  isDisabled: boolean;
  isLoading?: boolean;
  onPress?: () => void;
  type?: "button" | "submit" | "reset";
}

function ProactiveConnectButton({
  hasProactiveAccess,
  isDisabled,
  isLoading,
  onPress,
  type,
}: ProactiveConnectButtonProps) {
  const button = (
    <MDSButton
      type={type}
      variant={MDSButtonVariant.Default}
      isLoading={isLoading}
      isDisabled={isDisabled || !hasProactiveAccess}
      onPress={onPress}
    >
      Connect
    </MDSButton>
  );

  if (hasProactiveAccess) return button;

  return (
    <MDSTooltip config={PROACTIVE_REQUIRED_TOOLTIP}>
      <MDSTooltipTrigger>{button}</MDSTooltipTrigger>
    </MDSTooltip>
  );
}

interface PendingRefresh {
  previousCompletedAt: string | null;
  startedAt: number;
}

function isScanInProgress(connection: DisplayConnection): boolean {
  const startedAt = connection.last_scan_started_at;
  if (startedAt == null) return false;

  const completedAt = connection.last_scan_completed_at;
  return completedAt == null || startedAt > completedAt;
}

export const SettingsConnectedToolsPage = observer(() => {
  const { store } = useAppStore();
  const [serverUrl, setServerUrl] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [reauthorizingConnectionId, setReauthorizingConnectionId] = useState<string | null>(null);
  const [pendingRefreshes, setPendingRefreshes] = useState<Record<string, PendingRefresh>>({});

  const connections: DisplayConnection[] = store.spaceAccountConnections.all
    .map(connection => ({
      id: connection.modelData.associated_mcp_connection_id,
      ...connection.modelData,
    }))
    .filter(isDisplayConnection);
  const commercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
  const commerciallyPausedConnectionIds = new Set(
    commercialPlanSnapshot?.pausedConnectionIds ?? []
  );
  const compatibleCommercialCatalog = getCompatibleCommercialCatalog({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });
  const commercialConnectionAllowance =
    compatibleCommercialCatalog !== null && commercialPlanSnapshot !== null
      ? commercialPlanSnapshot.meters[CommercialMeterKey.ActiveConnections].allowance
      : null;
  const usesCommercialConnectionAllowance =
    compatibleCommercialCatalog !== null &&
    commercialPlanSnapshot !== null &&
    (compatibleCommercialCatalog.usageEnforcementEnabled ||
      commercialPlanSnapshot.migration?.state === CommercialMigrationState.NewCatalogActive ||
      (commercialConnectionAllowance !== null &&
        commercialConnectionAllowance > LEGACY_MAX_CONNECTIONS));
  const connectionLimit = usesCommercialConnectionAllowance
    ? commercialConnectionAllowance
    : LEGACY_MAX_CONNECTIONS;
  const connectionLimitReached = connectionLimit !== null && connections.length >= connectionLimit;
  const connectionLimitMessage = !usesCommercialConnectionAllowance
    ? "You've reached the five-connection limit. Disconnect one to add another."
    : connectionLimit === null
      ? ""
      : connectionLimit === 0
        ? "Connections aren't included in your current plan."
        : `You've reached your ${new Intl.NumberFormat().format(connectionLimit)} active-connection allowance. Disconnect one or choose a plan with more capacity.`;
  const availableFeaturedConnections = FEATURED_COMPOSIO_CONNECTIONS.filter(featuredApp => {
    return !connections.some(connection => {
      return getFeaturedComposioConnection(connection)?.title === featuredApp.title;
    });
  });
  useEffect(() => {
    setPendingRefreshes(current => {
      let changed = false;
      const next = { ...current };
      for (const [connectionId, pending] of Object.entries(current)) {
        const connection = connections.find(candidate => candidate.id === connectionId);
        const didFinishScan =
          connection?.last_scan_completed_at != null &&
          connection.last_scan_completed_at !== pending.previousCompletedAt;
        const didStartScan = connection != null && isScanInProgress(connection);
        if (!didFinishScan && !didStartScan) continue;

        delete next[connectionId];
        changed = true;
      }
      return changed ? next : current;
    });
  }, [connections]);

  useEffect(() => {
    const pendingEntries = Object.entries(pendingRefreshes);
    if (pendingEntries.length === 0) return;

    const now = Date.now();
    const delay = Math.max(
      0,
      Math.min(
        ...pendingEntries.map(([, pending]) => REFRESH_TIMEOUT_MS - (now - pending.startedAt))
      )
    );
    const timeoutId = window.setTimeout(() => {
      const expiredIds = new Set(
        pendingEntries
          .filter(([, pending]) => Date.now() - pending.startedAt >= REFRESH_TIMEOUT_MS)
          .map(([connectionId]) => connectionId)
      );
      if (expiredIds.size === 0) return;

      setPendingRefreshes(current =>
        Object.fromEntries(
          Object.entries(current).filter(([connectionId]) => !expiredIds.has(connectionId))
        )
      );
      toastModule.triggerToast({
        content: "The connection refresh is taking longer than expected.",
      });
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [pendingRefreshes]);

  const continueAuthorization = useCallback(async (connection: McpConnectionResponse) => {
    if (!connection.authorization_url) return;

    await windowModule.navigateToExternalAndPreserveHistory({
      url: new URL(connection.authorization_url),
    });
  }, []);

  const ensureProactiveAccess = useCallback(async () => {
    if (store.subscriptions.hasProactiveAccess) return true;

    await store.platformInfo.fetchPlatformInfo({ reconcileStripeSubscription: true });
    if (store.subscriptions.hasProactiveAccess) return true;

    store.subscriptions.openProactiveUpsell();
    return false;
  }, [store.platformInfo, store.subscriptions]);

  const handleConnect = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const normalizedInput = serverUrl.trim();
      if (!normalizedInput || connectionLimitReached) return;
      if (!store.subscriptions.hasProactiveAccess) return;

      setIsConnecting(true);
      trackMcpConnectionSetupAttempt();
      try {
        const response = await api.post("/v2/mcp-connections", {
          body: { server_url: normalizedInput },
        });
        if (response.error || !response.data) {
          const usageLimitRejection = parseCommercialUsageLimitRejection(response.error);
          if (usageLimitRejection) {
            store.subscriptions.openCommercialUsageLimit(usageLimitRejection);
            return;
          }

          throw new Error(
            getBackendApiErrorMessage(response.error) ?? "Could not connect to that server."
          );
        }

        setServerUrl("");
        await continueAuthorization(response.data);
      } catch (error) {
        if (error instanceof TypeError) {
          trackMcpConnectionSetupNetworkFailure();
        }
        toastModule.triggerToast({
          content: getMcpConnectErrorMessage(error),
        });
      } finally {
        setIsConnecting(false);
      }
    },
    [connectionLimitReached, continueAuthorization, serverUrl, store.subscriptions]
  );

  const handleRefresh = useCallback(async (connection: DisplayConnection) => {
    setPendingRefreshes(current => ({
      ...current,
      [connection.id]: {
        previousCompletedAt: connection.last_scan_completed_at,
        startedAt: Date.now(),
      },
    }));
    try {
      const response = await api.post("/v2/mcp-connections/{connection_id}/refresh", {
        params: { path: { connection_id: connection.id } },
      });
      if (response.error || !response.data) {
        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Could not refresh this connection."
        );
      }
    } catch (error) {
      setPendingRefreshes(current => {
        const next = { ...current };
        delete next[connection.id];
        return next;
      });
      toastModule.triggerToast({
        content: error instanceof Error ? error.message : "Could not refresh this connection.",
      });
    }
  }, []);

  const handleReconnect = useCallback(
    async (connection: DisplayConnection) => {
      if (getAuthorizationActionLabel(connection) === null) return;
      if (!(await ensureProactiveAccess())) return;
      setReauthorizingConnectionId(connection.id);
      try {
        const response = await api.post("/v2/mcp-connections/{connection_id}/authorization", {
          params: { path: { connection_id: connection.id } },
        });
        if (response.error || !response.data) {
          throw new Error(
            getBackendApiErrorMessage(response.error) ?? "Could not reconnect this service."
          );
        }
        await continueAuthorization(response.data);
      } catch (error) {
        toastModule.triggerToast({
          content: error instanceof Error ? error.message : "Could not reconnect this service.",
        });
      } finally {
        setReauthorizingConnectionId(null);
      }
    },
    [continueAuthorization, ensureProactiveAccess]
  );

  const handleFeaturedConnect = useCallback(
    (featuredApp: FeaturedComposioConnection) => {
      if (!store.subscriptions.hasProactiveAccess) return;

      store.settingsModal.openMcpComposioConnectionConfirmation({
        toolkit: featuredApp.providerAppIdPrefix,
        appTitle: featuredApp.title,
      });
    },
    [store.settingsModal, store.subscriptions]
  );

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader
        breadcrumb="Mem Agent"
        title="Connections"
        badgeLabel="In preview"
        badgeVariant={MDSBadgeVariant.Emphasis}
      />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledConnectionsContentStack>
            <StyledSubExplanation variant={MDSTextVariant.ParagraphS}>
              Connect Mem Agent to the tasks and projects across your life.
              {!store.subscriptions.hasProactiveAccess && (
                <>
                  <br />
                  <MDSLink
                    onPress={() => store.settingsModal.navigateToTab(SettingsTabId.MemAgentPage)}
                  >
                    Get Proactive
                  </MDSLink>
                </>
              )}
            </StyledSubExplanation>

            <StyledConnectionSection aria-label="Connected services">
              <StyledListHeader
                variant={MDSTextVariant.TextXs}
                weight={MDSTextWeight.Smedium}
                color={MDSTextColor.Secondary}
              >
                CONNECTED
              </StyledListHeader>

              {connections.length > 0 ? (
                <StyledCardList>
                  {connections.map(connection => {
                    const displayName = getConnectionDisplayName(connection);
                    const featuredApp = getFeaturedComposioConnection(connection);
                    const isCommerciallyPaused = commerciallyPausedConnectionIds.has(connection.id);
                    const statusDescription = isCommerciallyPaused
                      ? "Monitoring is paused because this connection exceeds your plan allowance."
                      : getConnectionStatusDescription(connection);
                    const isRefreshing = pendingRefreshes[connection.id] !== undefined;
                    const isScanning = isRefreshing || isScanInProgress(connection);
                    const lastSynced = isScanning
                      ? "Syncing..."
                      : formatCalendarSyncTime(connection.last_scan_completed_at);
                    const authorizationActionLabel = getAuthorizationActionLabel(connection);
                    const isReauthorizing = reauthorizingConnectionId === connection.id;

                    return (
                      <StyledConnectionCard
                        key={connection.id}
                        as="section"
                        aria-label={displayName}
                      >
                        <StyledConnectionPrimary>
                          <StyledConnectionIcon>
                            {featuredApp ? (
                              <StyledConnectionLogo src={featuredApp.iconUrl} alt="" />
                            ) : (
                              <MDSIcon
                                kind={MDSIconKind.PuzzlePiece}
                                color={MDSIconColor.Primary}
                                size={12}
                                boxSize={32}
                              />
                            )}
                          </StyledConnectionIcon>
                          <StyledConnectionCopy>
                            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                              {displayName}
                            </MDSText>
                            {statusDescription ? (
                              <MDSText
                                variant={MDSTextVariant.TextS}
                                color={MDSTextColor.Secondary}
                              >
                                {statusDescription}
                              </MDSText>
                            ) : null}
                            {isCommerciallyPaused ? (
                              <MDSLink
                                onPress={() =>
                                  store.settingsModal.navigateToTab(
                                    SettingsTabId.SubscriptionManagementPage
                                  )
                                }
                              >
                                Review plans or Extra Usage
                              </MDSLink>
                            ) : null}
                          </StyledConnectionCopy>
                          <StyledActions>
                            {authorizationActionLabel && (
                              <MDSButton
                                variant={MDSButtonVariant.Contrast}
                                isLoading={isReauthorizing}
                                isDisabled={isReauthorizing}
                                onPress={() => void handleReconnect(connection)}
                              >
                                {authorizationActionLabel}
                              </MDSButton>
                            )}
                            {lastSynced && (
                              <StyledSyncStatus>
                                <MDSIcon
                                  kind={isScanning ? MDSIconKind.Spinner : MDSIconKind.Check}
                                  size={10}
                                  color={
                                    isScanning ? MDSIconColor.Secondary : MDSIconColor.Positive
                                  }
                                />
                                <MDSText
                                  variant={MDSTextVariant.TextXs}
                                  color={MDSTextColor.Secondary}
                                >
                                  {lastSynced}
                                </MDSText>
                              </StyledSyncStatus>
                            )}
                            <MDSIconButton
                              aria-label={`Refresh ${displayName}`}
                              iconKind={isScanning ? MDSIconKind.Spinner : MDSIconKind.Refresh}
                              size={MDSIconButtonSize.Medium}
                              isDisabled={
                                isCommerciallyPaused ||
                                connection.status === "authorizing" ||
                                isScanning ||
                                isReauthorizing
                              }
                              onPress={() => void handleRefresh(connection)}
                            />
                            <MDSActionMenuDropdown
                              contentList={[
                                ...(authorizationActionLabel
                                  ? [
                                      {
                                        id: `reconnect-${connection.id}`,
                                        kind: MDSMenuContentItemKind.Item as const,
                                        label: authorizationActionLabel,
                                        icon: MDSIconKind.Refresh,
                                        isDisabled: isReauthorizing,
                                        onClick: () => void handleReconnect(connection),
                                      },
                                    ]
                                  : []),
                                {
                                  id: `disconnect-${connection.id}`,
                                  kind: MDSMenuContentItemKind.Item,
                                  label: "Disconnect",
                                  icon: MDSIconKind.Ban,
                                  onClick: () =>
                                    store.settingsModal.openMcpConnectionDisconnectDialog({
                                      id: connection.id,
                                      serverUrl: connection.server_url,
                                      serverName: connection.server_name,
                                    }),
                                },
                              ]}
                              placement="bottom right"
                            >
                              <MDSIconButton
                                slot={MDSMenuDropdownSlotName.Trigger}
                                aria-label={`More actions for ${displayName}`}
                                iconKind={MDSIconKind.More}
                                size={MDSIconButtonSize.Medium}
                              />
                            </MDSActionMenuDropdown>
                          </StyledActions>
                        </StyledConnectionPrimary>
                      </StyledConnectionCard>
                    );
                  })}
                </StyledCardList>
              ) : (
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  No connected services yet.
                </MDSText>
              )}
            </StyledConnectionSection>

            <StyledConnectionSection aria-label="Available services">
              <StyledListHeader
                variant={MDSTextVariant.TextXs}
                weight={MDSTextWeight.Smedium}
                color={MDSTextColor.Secondary}
              >
                AVAILABLE
              </StyledListHeader>

              <StyledCardList>
                {availableFeaturedConnections.map(featuredApp => {
                  return (
                    <StyledConnectionCard
                      key={featuredApp.title}
                      as="section"
                      aria-label={featuredApp.title}
                    >
                      <StyledConnectionPrimary>
                        <StyledConnectionIcon>
                          <StyledConnectionLogo src={featuredApp.iconUrl} alt="" />
                        </StyledConnectionIcon>
                        <StyledConnectionCopy>
                          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                            {featuredApp.title}
                          </MDSText>
                          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                            {featuredApp.description}
                          </MDSText>
                        </StyledConnectionCopy>
                        <StyledActions>
                          <ProactiveConnectButton
                            hasProactiveAccess={store.subscriptions.hasProactiveAccess}
                            isDisabled={connectionLimitReached}
                            onPress={() => void handleFeaturedConnect(featuredApp)}
                          />
                        </StyledActions>
                      </StyledConnectionPrimary>
                    </StyledConnectionCard>
                  );
                })}

                <StyledConnectionCard as="form" aria-label="Custom MCP" onSubmit={handleConnect}>
                  <StyledConnectionPrimary>
                    <StyledCustomMcpCopy>
                      <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                        Custom MCP
                      </MDSText>
                      <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                        Enter its URL
                      </MDSText>
                    </StyledCustomMcpCopy>
                    <StyledCustomMcpInput
                      aria-label="MCP server URL"
                      type="url"
                      inputMode="url"
                      placeholder="https://mcp.example.com"
                      value={serverUrl}
                      onChange={event => setServerUrl(event.target.value)}
                      required
                    />
                    <StyledActions>
                      <ProactiveConnectButton
                        type="submit"
                        hasProactiveAccess={store.subscriptions.hasProactiveAccess}
                        isLoading={isConnecting}
                        isDisabled={!serverUrl.trim() || connectionLimitReached}
                      />
                    </StyledActions>
                  </StyledConnectionPrimary>
                </StyledConnectionCard>

                {connectionLimitReached ? (
                  <StyledLimitNotice>
                    <MDSIcon
                      kind={MDSIconKind.InfoCircle}
                      color={MDSIconColor.Secondary}
                      size={14}
                    />
                    <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                      {connectionLimitMessage}
                    </MDSText>
                    {usesCommercialConnectionAllowance ? (
                      <MDSLink
                        onPress={() =>
                          store.settingsModal.navigateToTab(
                            SettingsTabId.SubscriptionManagementPage
                          )
                        }
                      >
                        Review plans
                      </MDSLink>
                    ) : null}
                  </StyledLimitNotice>
                ) : null}
              </StyledCardList>
            </StyledConnectionSection>
          </StyledConnectionsContentStack>
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledConnectionsContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
  padding-bottom: ${({ theme }) => theme.spacing[8]};
  width: 100%;
  max-width: 700px;
  container-type: inline-size;
  container-name: connections-settings-content;
`;

const StyledListHeader = styled(MDSText)`
  letter-spacing: 0.02em;
`;

const StyledConnectionSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StyledCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledConnectionCard = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.pane};
`;

const StyledConnectionPrimary = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  gap: ${({ theme }) => theme.spacing[2]};
  min-height: 80px;
  padding: ${({ theme }) =>
    `${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[3]}`};
`;

const StyledConnectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
`;

const StyledConnectionLogo = styled.img`
  display: block;
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const StyledConnectionCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledCustomMcpCopy = styled(StyledConnectionCopy)`
  flex: 0 0 128px;
`;

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledSyncStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding-right: ${({ theme }) => theme.spacing[2]};
`;

const StyledLimitNotice = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.pane};
`;

const StyledInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]}`};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  outline: none;
  background: ${({ theme }) => theme.colors.background.card.default};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;

  &:focus {
    border-color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const StyledCustomMcpInput = styled(StyledInput)`
  flex: 1 1 auto;
  width: auto;
`;
